import {
  VegaBox,
  VegaButton,
  VegaCard,
  VegaChip,
  VegaFlex,
  VegaFont,
  VegaGrid,
  VegaTable,
} from '@globalpayments/vega-react';
import type {
  VegaTableColumnConfig,
  VegaTableDataSourceItem,
  VegaTableRowClickPropType,
} from '@globalpayments/vega/dist/types/components/vega-table/interface';

import { ComponentStatusCheck } from '../../../types/componentStatus';
import styles from '../index.module.css';
import { descriptionFromBuildDate, statusColor, statusLabel } from './statusHelpers';

type ComponentStatusTableRecord = VegaTableDataSourceItem & ComponentStatusCheck;

interface ComponentStatusGroupProps {
  groupName: string;
  components: ComponentStatusCheck[];
  onOpenDetails: (component: ComponentStatusCheck) => void;
}

const stableCellProps = {
  alignItems: 'center',
  direction: 'row',
  height: 'size-32',
  width: '100%',
};
const centeredCellProps = { ...stableCellProps, justifyContent: 'center' };

const columns: VegaTableColumnConfig[] = [
  {
    key: 'name',
    label: 'Name',
    prop: 'name',
    align: 'left',
    width: '30%',
    render: (createElement, _value, record) => {
      const component = record as ComponentStatusTableRecord;
      return createElement('vega-flex', { ...stableCellProps, gap: 'size-8' }, [
        createElement('vega-font', { as: 'span', variant: 'font-p2-short' }, component.name),
        createElement('vega-chip', {
          chipType: 'label',
          size: 'extra-small',
          text: component.componentType,
          variant: 'outlined',
        }),
      ]);
    },
  },
  {
    key: 'buildVersion',
    label: 'Build Version',
    align: 'center',
    width: '25%',
    render: (createElement, _value, record) =>
      createElement(
        'vega-flex',
        centeredCellProps,
        (record as ComponentStatusTableRecord).statusResult?.buildVersion ?? '',
      ),
  },
  {
    key: 'buildDate',
    label: 'Build Date',
    align: 'center',
    width: '30%',
    render: (createElement, _value, record) => {
      const buildDate = (record as ComponentStatusTableRecord).statusResult?.buildDate ?? '';
      return createElement(
        'vega-flex',
        centeredCellProps,
        createElement(
          'vega-tooltip',
          { tooltipText: buildDate },
          createElement('vega-font', { as: 'span', variant: 'font-p2-short' }, descriptionFromBuildDate(buildDate)),
        ),
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    align: 'center',
    width: '15%',
    render: (createElement, _value, record) => {
      const component = record as ComponentStatusTableRecord;
      if (component.isLoadingStatus) {
        const currentStatus = component.statusResult?.status;
        const loadingIndicator = createElement('vega-loading-indicator', {
          'aria-label': 'Loading status',
          mode: 'indeterminate',
          shape: 'circle',
          size: 'micro',
        });
        if (!currentStatus) return createElement('vega-flex', centeredCellProps, loadingIndicator);

        return createElement('vega-flex', { ...centeredCellProps, gap: 'size-8' }, [
          createElement('vega-chip', {
            'aria-label': currentStatus,
            chipType: 'status',
            size: 'extra-small',
            statusColor: statusColor(currentStatus),
            text: currentStatus,
            variant: 'outlined',
          }),
          loadingIndicator,
        ]);
      }

      const status = statusLabel(component);
      return createElement(
        'vega-flex',
        centeredCellProps,
        createElement('vega-chip', {
          'aria-label': status,
          chipType: 'status',
          size: 'extra-small',
          statusColor: statusColor(component.statusResult?.status),
          text: status,
          variant: 'outlined',
        }),
      );
    },
  },
];

/** Renders a component-status group as a desktop table and responsive card list. */
const ComponentStatusGroup = ({ groupName, components, onOpenDetails }: ComponentStatusGroupProps) => {
  const handleTableRowClick = (event: CustomEvent<VegaTableRowClickPropType>) => {
    onOpenDetails(event.detail.record as ComponentStatusTableRecord);
  };

  return (
    <VegaCard className={styles.statusGroup} padding="size-16" variant="border">
      <VegaFlex direction="col" gap="size-16">
        <VegaFlex alignItems="center" flexWrap="wrap" gap="size-12" justifyContent="space-between">
          <VegaFont as="h3" color="text-secondary" variant="font-h5">
            {groupName}
          </VegaFont>
          <VegaChip
            chipType="label"
            size="extra-small"
            text={`${components.length} ${components.length === 1 ? 'component' : 'components'}`}
            variant="outlined"
          />
        </VegaFlex>
        <VegaBox className={styles.statusCheckTable}>
          <VegaTable
            aria-label="ComponentStatusCheckTable"
            columns={columns}
            dataSource={components as ComponentStatusTableRecord[]}
            density="compact"
            emptyDescription="No component checks are configured for this group."
            onVegaRowClick={handleTableRowClick}
            rowClickable
            rowDivider
            rowKey="componentStatusCheckId"
            striped
          />
        </VegaBox>
        <VegaFlex className={styles.componentStatusCards} direction="col" gap="size-8">
          {components.map((component) => {
            const status = statusLabel(component);
            return (
              <VegaCard
                className={styles.componentStatusCard}
                key={component.componentStatusCheckId}
                padding="size-16"
                variant="border">
                <VegaFlex direction="col" gap="size-12">
                  <VegaFlex alignItems="center" gap="size-8" justifyContent="space-between">
                    <VegaFlex alignItems="center" flexWrap="wrap" gap="size-8">
                      <VegaFont as="span" variant="font-p2-short">
                        {component.name}
                      </VegaFont>
                      <VegaChip chipType="label" size="extra-small" text={component.componentType} variant="outlined" />
                    </VegaFlex>
                    <VegaFlex alignItems="center" gap="size-8">
                      <VegaChip
                        aria-label={status}
                        chipType="status"
                        size="extra-small"
                        statusColor={statusColor(component.statusResult?.status)}
                        text={status}
                        variant="outlined"
                      />
                      <VegaButton
                        aria-label={`View ${component.name} details`}
                        label="Details"
                        onVegaClick={() => onOpenDetails(component)}
                        size="small"
                        variant="secondary"
                      />
                    </VegaFlex>
                  </VegaFlex>
                  <VegaGrid column={2} gap="size-16">
                    <ComponentStatusCardField
                      label="Build Version"
                      value={component.statusResult?.buildVersion ?? '-'}
                    />
                    <ComponentStatusCardField
                      label="Build Date"
                      value={descriptionFromBuildDate(component.statusResult?.buildDate) || '-'}
                    />
                  </VegaGrid>
                </VegaFlex>
              </VegaCard>
            );
          })}
        </VegaFlex>
      </VegaFlex>
    </VegaCard>
  );
};

interface ComponentStatusCardFieldProps {
  label: string;
  value: string;
}

const ComponentStatusCardField = ({ label, value }: ComponentStatusCardFieldProps) => (
  <VegaFlex direction="col" gap="size-4">
    <VegaFont as="span" color="text-secondary" variant="font-field-label-em">
      {label}
    </VegaFont>
    <VegaFont as="span" variant="font-p2-short">
      {value}
    </VegaFont>
  </VegaFlex>
);

export default ComponentStatusGroup;
